var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import { ArrowRight } from '@sumup/icons';
import styled from '../../../../styles/styled';
import { cx, focusVisible } from '../../../../styles/style-mixins';
import { useClickEvent } from '../../../../hooks/useClickEvent';
import { useComponents } from '../../../ComponentsContext';
import Body from '../../../Body';
import { Skeleton } from '../../../Skeleton';
const anchorStyles = ({ theme }) => css `
  position: relative;
  display: flex;
  align-items: center;
  height: 80px;
  width: 100%;
  background: none;
  border: none;
  outline: none;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  color: var(--cui-fg-normal);
  padding: ${theme.spacings.giga};
  transition: color ${theme.transitions.default},
    background-color ${theme.transitions.default};

  &:hover {
    background-color: var(--cui-bg-normal-hovered);
    color: var(--cui-fg-normal-hovered);
  }

  &:active {
    background-color: var(--cui-bg-normal-pressed);
    color: var(--cui-fg-normal-pressed);
  }

  &:disabled,
  &[disabled] {
    pointer-events: none;
    background-color: var(--cui-bg-normal-disabled);
    color: var(--cui-fg-normal-disabled);
  }

  ${theme.mq.untilTera} {
    margin-bottom: ${theme.borderWidth.kilo};

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 100%;
      right: ${theme.spacings.giga};
      left: ${theme.spacings.giga};
      width: calc(100% - 2 * ${theme.spacings.giga});
      border-bottom: ${theme.borderWidth.kilo} solid var(--cui-border-divider);
      transition: width ${theme.transitions.default},
        right ${theme.transitions.default}, left ${theme.transitions.default};
    }
  }

  ${theme.mq.tera} {
    height: 48px;
    width: 220px;
    padding: ${theme.spacings.kilo};
    margin-bottom: ${theme.spacings.kilo};
  }
`;
const anchorActiveStyles = ({ isActive }) => isActive &&
    css `
    color: var(--cui-fg-accent);

    &:hover {
      background-color: var(--cui-bg-accent-hovered);
    }

    &:active {
      background-color: var(--cui-bg-accent-pressed);
    }
  `;
const anchorOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    ${theme.mq.untilTera} {
      &::after {
        right: 0;
        left: 0;
        width: 100%;
      }
    }
  `;
const Anchor = styled('a', {
    shouldForwardProp: (prop) => isPropValid(prop),
})(focusVisible('inset'), anchorStyles, anchorActiveStyles, anchorOpenStyles);
const iconStyles = (theme) => css `
  position: relative;
  flex-shrink: 0;
  width: ${theme.iconSizes.mega};
  height: ${theme.iconSizes.mega};
  margin-right: ${theme.spacings.kilo};
`;
const iconWithBadgeStyles = (theme) => css `
  &::before {
    display: block;
    content: '';
    position: absolute;
    top: -8px;
    right: -8px;
    width: 10px;
    height: 10px;
    background-color: var(--cui-fg-promo);
    border-radius: ${theme.borderRadius.circle};
  }
`;
const suffixStyles = (theme) => css `
  flex-shrink: 0;
  width: ${theme.iconSizes.kilo};
  height: ${theme.iconSizes.kilo};
  margin-left: auto;
  transition: transform ${theme.transitions.default};
`;
const externalIconStyles = css `
  transform: rotate(-45deg);
`;
const labelStyles = ({ theme }) => css `
  ${theme.mq.untilTera} {
    font-size: ${theme.typography.headline.two.fontSize};
    line-height: ${theme.typography.headline.two.lineHeight};
  }
`;
const Label = styled(Body)(labelStyles);
export function PrimaryLink(_a) {
    var { icon: Icon, label, onClick, isActive, isOpen, isExternal, suffix: Suffix, badge, tracking, secondaryGroups } = _a, props = __rest(_a, ["icon", "label", "onClick", "isActive", "isOpen", "isExternal", "suffix", "badge", "tracking", "secondaryGroups"]);
    const { Link } = useComponents();
    const handleClick = useClickEvent(onClick, tracking, 'primary-link');
    const suffix = Suffix && _jsx(Suffix, { css: suffixStyles, role: "presentation" });
    const isExternalLink = isExternal || props.target === '_blank';
    return (_jsxs(Anchor, Object.assign({}, props, { onClick: handleClick, isActive: isActive, isOpen: isOpen, "aria-current": isActive ? 'page' : undefined, as: props.href ? Link : 'button' }, { children: [_jsx(Skeleton, Object.assign({ css: cx(iconStyles, badge && iconWithBadgeStyles) }, { children: _jsx(Icon, { role: "presentation", size: "24" }) })), _jsx(Skeleton, { children: _jsx(Label, Object.assign({ variant: isActive || isOpen ? 'highlight' : undefined, as: "span" }, { children: label })) }), isExternalLink && (_jsx(ArrowRight, { size: "16", role: "presentation", css: cx(suffixStyles, externalIconStyles) })), suffix] })));
}
